import { wait } from "@/utils";
import { PendingEventsStore, usePendingEvents } from "camera/store/pendingEvents";
import { createCameraHistoryEvent } from "../cloud/media";
import LocalEvents from "../LocalEvents";
import { MediaUploadManager } from "../upload/MediaUploadManager";

const RETRY_TIMEOUT = 3000;
const FAILURE_THRESHOLD = 3;
const END_COVERAGE_GRACE = 15000;
const { getOldestEvent, removeEvent, moveEventToTheEnd } = usePendingEvents.getState();

export default class EventUploader {
  static state: "uploading" | "idle" = "idle";
  private static unsubscribe: Cb;
  private static failedAttempt = 0;

  static start = async () => {
    await LocalEvents.closeAndAddUnfinishedEventsToQueue();
    await LocalEvents.deleteBrokenEvents();
    this.unsubscribe = usePendingEvents.subscribe(this.onStoreUpdate);
    if (usePendingEvents.getState().events.length > 0) this.upload();
  };

  private static onStoreUpdate = (store: PendingEventsStore) => {
    if (store.events.length > 0 && this.state !== "uploading") this.upload();
  };

  // The server never finishes an event whose end is not covered by uploaded
  // media (previewEndDate <= lastSegmentDate has no tolerance). Hold finished
  // events until the post-roll segments catch up; when no more media is coming,
  // shrink the event to the media we actually have — same rule as
  // LocalEvents.closeAndAddUnfinishedEventsToQueue.
  private static ensureEndIsCovered = (event: StoreCameraEvent): Promise<StoreCameraEvent> => {
    const isCovered = () => {
      const lastSegmentEnd = usePendingEvents.getState().lastUploadedSegmentEndTime;
      return !lastSegmentEnd || lastSegmentEnd >= event.end;
    };
    if (isCovered()) return Promise.resolve(event);

    return new Promise((resolve) => {
      const capToLastMedia = (): StoreCameraEvent => {
        const lastSegmentEnd = usePendingEvents.getState().lastUploadedSegmentEndTime;
        if (!lastSegmentEnd || lastSegmentEnd < event.start) return event;
        log.events("Capping event end to last media marked for upload", {
          uniqueId: event.uniqueId,
          from: event.end,
          to: lastSegmentEnd
        });
        return { ...event, end: lastSegmentEnd };
      };

      const finish = (result: StoreCameraEvent) => {
        clearTimeout(graceTimer);
        unsubscribe();
        resolve(result);
      };

      const unsubscribe = usePendingEvents.subscribe(() => {
        if (isCovered()) finish(event);
        else if (!MediaUploadManager.isRecording()) finish(capToLastMedia());
      });

      const graceTimer = setTimeout(
        () => finish(capToLastMedia()),
        Math.max(0, event.end + END_COVERAGE_GRACE - Date.now())
      );

      if (!MediaUploadManager.isRecording()) finish(capToLastMedia());
    });
  };

  private static upload = async () => {
    const event = getOldestEvent();
    if (!event) {
      log.events("Finished uploading all events");
      this.state = "idle";
      return;
    }
    try {
      this.state = "uploading";
      const eventToUpload = event.finished ? await this.ensureEndIsCovered(event) : event;
      log.events("About to upload event:", eventToUpload);
      const res = await createCameraHistoryEvent({ cameraEvent: eventToUpload });
      if (!res) throw Error("Failed 'createCameraHistoryEvent'");
      log.events("Uploaded event", event);

      this.failedAttempt = 0;
      const eventsInStore = removeEvent(event.storeId);
      if (eventsInStore.length > 0) this.upload();
      else this.state = "idle";
    } catch (err) {
      log.events("Failed event upload for event", event, err);
      this.failedAttempt += 1;

      if (this.failedAttempt >= FAILURE_THRESHOLD) {
        this.failedAttempt = 0;
        moveEventToTheEnd(event.storeId);
        this.upload();
      } else {
        await wait(RETRY_TIMEOUT * this.failedAttempt);
        this.upload();
      }
    }
  };

  static destroy = () => {
    this.unsubscribe();
  };
}
